import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/api/client";
import type {
  AgentDetail,
  AgentInstallResult,
  MarketplaceAgentDetail,
  MarketplaceList,
  MarketplaceSort,
  MyAgent,
} from "@/api/types";

interface MarketplaceFilters {
  q?: string;
  sort?: MarketplaceSort;
  limit?: number;
  offset?: number;
}

const KEY = ["marketplace"] as const;
const KEY_DETAIL = (id: string) => ["marketplace", id] as const;
const KEY_MY_INSTALLED = ["me", "installed-agents"] as const;
const KEY_MY_CREATED = ["me", "created-agents"] as const;

export function useMarketplace(filters: MarketplaceFilters = {}) {
  return useQuery({
    queryKey: [...KEY, filters],
    queryFn: () => {
      const params: Record<string, string | number> = {};
      if (filters.q) params.q = filters.q;
      if (filters.sort) params.sort = filters.sort;
      if (filters.limit) params.limit = filters.limit;
      if (filters.offset) params.offset = filters.offset;
      return api.GET<MarketplaceList>("/marketplace", { params });
    },
  });
}

export function useMarketplaceAgent(agentId: string) {
  return useQuery({
    queryKey: KEY_DETAIL(agentId),
    queryFn: () => api.GET<MarketplaceAgentDetail>(`/marketplace/${agentId}`),
    enabled: Boolean(agentId),
  });
}

export function useInstallAgent(agentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.POST<AgentInstallResult>(`/agents/${agentId}/install`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
      void qc.invalidateQueries({ queryKey: KEY_DETAIL(agentId) });
      void qc.invalidateQueries({ queryKey: KEY_MY_INSTALLED });
    },
  });
}

export function useUninstallAgent(agentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.DELETE<void>(`/agents/${agentId}/install`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
      void qc.invalidateQueries({ queryKey: KEY_DETAIL(agentId) });
      void qc.invalidateQueries({ queryKey: KEY_MY_INSTALLED });
    },
  });
}

export function useMyInstalledAgents() {
  return useQuery({
    queryKey: KEY_MY_INSTALLED,
    queryFn: () => api.GET<MyAgent[]>("/me/installed-agents"),
  });
}

export function useMyCreatedAgents() {
  return useQuery({
    queryKey: KEY_MY_CREATED,
    queryFn: () => api.GET<AgentDetail[]>("/me/created-agents"),
  });
}
